import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import channelService from "../services/channelService";

// Get all channels
export const getChannels = createAsyncThunk("channels/getChannels", async (_, { rejectWithValue }) => {
  try {
    return await channelService.getAll();
  } catch (error) {
    return rejectWithValue(error.response?.data?.message || error.message);
  }
});

// Get a single channel
export const getChannel = createAsyncThunk("channels/getChannel", async (id, { rejectWithValue }) => {
  try {
    return await channelService.getById(id);
  } catch (error) {
    return rejectWithValue(error.response?.data?.message || error.message);
  }
});

// Create a new channel
export const createChannel = createAsyncThunk("channels/createChannel", async (channelData, { rejectWithValue }) => {
  try {
    return await channelService.create(channelData);
  } catch (error) {
    return rejectWithValue(error.response?.data?.message || error.message);
  }
});

// Update a channel
export const updateChannel = createAsyncThunk(
  "channels/updateChannel",
  async ({ id, channelData }, { rejectWithValue }) => {
    try {
      return await channelService.update(id, channelData);
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || error.message);
    }
  },
);

// Delete a channel
export const deleteChannel = createAsyncThunk("channels/deleteChannel", async (id, { rejectWithValue }) => {
  try {
    await channelService.remove(id);
    return id;
  } catch (error) {
    return rejectWithValue(error.response?.data?.message || error.message);
  }
});

// Get all users with access to a channel
export const getChannelUsers = createAsyncThunk("channels/getChannelUsers", async (channelId, { rejectWithValue }) => {
  try {
    return await channelService.getChannelUsers(channelId);
  } catch (error) {
    return rejectWithValue(error.response?.data?.message || error.message);
  }
});

// Set/update a user's access to a channel
export const setChannelUserAccess = createAsyncThunk(
  "channels/setChannelUserAccess",
  async ({ channelId, userId }, { rejectWithValue }) => {
    try {
      return await channelService.setChannelUserAccess(channelId, userId);
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || error.message);
    }
  },
);

// Remove a user's access to a channel
export const removeChannelUserAccess = createAsyncThunk(
  "channels/removeChannelUserAccess",
  async ({ channelId, userId }, { rejectWithValue }) => {
    try {
      await channelService.removeChannelUserAccess(channelId, userId);
      return userId;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || error.message);
    }
  },
);

const initialState = {
  channels: [],
  currentChannel: null,
  loading: false,
  error: null,
  success: false,
  channelUsers: [],
  channelUsersLoading: false,
  channelUsersError: null,
};

const channelsSlice = createSlice({
  name: "channels",
  initialState,
  reducers: {
    clearError: (state) => {
      state.error = null;
    },
    clearSuccess: (state) => {
      state.success = false;
    },
    setCurrentChannel: (state, action) => {
      state.currentChannel = action.payload;
    },
    clearCurrentChannel: (state) => {
      state.currentChannel = null;
    },
  },
  extraReducers: (builder) => {
    builder
      // Get all channels
      .addCase(getChannels.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(getChannels.fulfilled, (state, action) => {
        state.loading = false;
        state.channels = action.payload;
      })
      .addCase(getChannels.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      // Get a single channel
      .addCase(getChannel.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(getChannel.fulfilled, (state, action) => {
        state.loading = false;
        state.currentChannel = action.payload;
      })
      .addCase(getChannel.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      // Create a new channel
      .addCase(createChannel.pending, (state) => {
        state.loading = true;
        state.error = null;
        state.success = false;
      })
      .addCase(createChannel.fulfilled, (state, action) => {
        state.loading = false;
        state.channels.push(action.payload);
        state.success = true;
      })
      .addCase(createChannel.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      // Update a channel
      .addCase(updateChannel.pending, (state) => {
        state.loading = true;
        state.error = null;
        state.success = false;
      })
      .addCase(updateChannel.fulfilled, (state, action) => {
        state.loading = false;
        state.channels = state.channels.map((channel) => (channel.id === action.payload.id ? action.payload : channel));
        state.currentChannel = action.payload;
        state.success = true;
      })
      .addCase(updateChannel.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      // Delete a channel
      .addCase(deleteChannel.pending, (state) => {
        state.loading = true;
        state.error = null;
        state.success = false;
      })
      .addCase(deleteChannel.fulfilled, (state, action) => {
        state.loading = false;
        state.channels = state.channels.filter((channel) => channel.id !== action.payload);
        state.success = true;
      })
      .addCase(deleteChannel.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      // Get channel users
      .addCase(getChannelUsers.pending, (state) => {
        state.channelUsersLoading = true;
        state.channelUsersError = null;
      })
      .addCase(getChannelUsers.fulfilled, (state, action) => {
        state.channelUsersLoading = false;
        state.channelUsers = action.payload;
      })
      .addCase(getChannelUsers.rejected, (state, action) => {
        state.channelUsersLoading = false;
        state.channelUsersError = action.payload;
      })

      // Set channel user access
      .addCase(setChannelUserAccess.pending, (state) => {
        state.channelUsersLoading = true;
        state.channelUsersError = null;
      })
      .addCase(setChannelUserAccess.fulfilled, (state, action) => {
        state.channelUsersLoading = false;
        if (action.payload) {
          const index = state.channelUsers.findIndex((u) => u.user_id === action.payload.user_id);
          if (index !== -1) {
            state.channelUsers[index] = action.payload;
          } else {
            state.channelUsers.push(action.payload);
          }
        }
      })
      .addCase(setChannelUserAccess.rejected, (state, action) => {
        state.channelUsersLoading = false;
        state.channelUsersError = action.payload;
      })

      // Remove channel user access
      .addCase(removeChannelUserAccess.pending, (state) => {
        state.channelUsersLoading = true;
        state.channelUsersError = null;
      })
      .addCase(removeChannelUserAccess.fulfilled, (state, action) => {
        state.channelUsersLoading = false;
        state.channelUsers = state.channelUsers.filter((u) => u.user_id !== action.payload);
      })
      .addCase(removeChannelUserAccess.rejected, (state, action) => {
        state.channelUsersLoading = false;
        state.channelUsersError = action.payload;
      });
  },
});

export const { clearError, clearSuccess, setCurrentChannel, clearCurrentChannel } = channelsSlice.actions;

export default channelsSlice.reducer;
